import { filterValidLocalizationMessages } from './localizationMessages'

/**
 * Locale switching for the report pages, kept free of Nuxt runtime imports so
 * it can be unit tested and shared between the public Web Report page and the
 * tokenised project report page.
 */

/**
 * Structural view of the vue-i18n composer, satisfied by both `$i18n` and
 * `useI18n()`. Generic over the locale union those narrow their arguments to,
 * since the tags handled here arrive as plain strings at runtime.
 */
export interface ReportI18n<TLocale extends string = string> {
  locale: { value: TLocale }
  locales?: { value: readonly (string | { code?: string })[] }
  localeCodes?: { value: readonly TLocale[] }
  setLocale: (tag: TLocale) => Promise<unknown> | unknown
  getLocaleMessage: (tag: TLocale) => Record<string, unknown>
  mergeLocaleMessage: (tag: TLocale, messages: Record<string, unknown>) => void
}

export interface ReportLocaleResult {
  /** The locale actually in effect once the switch settled. */
  locale: string
  /** Whether the requested tag was applied. */
  switched: boolean
}

const DEFAULT_LOCALE = 'en-US'

function localeCode(entry: string | { code?: string } | undefined | null) {
  if (typeof entry === 'string')
    return entry
  if (typeof entry === 'object' && entry !== null)
    return entry.code
  return undefined
}

/**
 * First configured locale code, for use when nothing else identifies a language.
 * Locale entries are `{ code, language, name }` objects on @nuxtjs/i18n v10 —
 * they carry no `iso` property.
 */
export function resolveFallbackLocaleCode(
  locales: readonly (string | { code?: string })[] | undefined,
  defaultLocale: string = DEFAULT_LOCALE,
): string {
  return localeCode(locales?.[0]) ?? defaultLocale
}

/** Whether `tag` is one of the locales configured in nuxt.config.ts. */
export function isKnownLocale<TLocale extends string>(
  i18n: ReportI18n<TLocale>,
  tag: string,
): boolean {
  const codes: readonly string[] | undefined
    = i18n.localeCodes?.value
      ?? i18n.locales?.value.map(localeCode).filter(code => code !== undefined)

  // Without a configured list to check against, let vue-i18n make the call.
  return codes === undefined || codes.includes(tag)
}

/**
 * Switch to a report's published language and layer its backend-managed UI
 * strings on top of the bundled ones.
 *
 * `setLocale` resolves only once the locale's messages have loaded, so awaiting
 * it is what keeps callers from rendering or merging against an empty message
 * store. It also returns silently for an unrecognised tag, so the merge is
 * keyed off `tag` and gated on the switch having actually taken effect —
 * merging into whatever locale happens to be active would splice one language's
 * strings into another's message store for the rest of the session.
 */
export async function applyReportLocale<TLocale extends string>(
  i18n: ReportI18n<TLocale>,
  tag: string | undefined | null,
  uiLocalizations?: Record<string, string> | null,
): Promise<ReportLocaleResult> {
  if (!tag)
    return { locale: i18n.locale.value, switched: false }

  if (!isKnownLocale(i18n, tag)) {
    console.warn(`[i18n] Report requested unsupported locale "${tag}", keeping ${i18n.locale.value}`)
    return { locale: i18n.locale.value, switched: false }
  }

  // Checked against the configured locales above.
  const known = tag as TLocale

  try {
    await i18n.setLocale(known)
  }
  catch (error) {
    console.error(`[i18n] Failed to load locale "${tag}", falling back to ${DEFAULT_LOCALE}:`, error)
    try {
      await i18n.setLocale(DEFAULT_LOCALE as TLocale)
    }
    catch (fallbackError) {
      console.error(`[i18n] Failed to load fallback locale ${DEFAULT_LOCALE}:`, fallbackError)
    }
    return { locale: i18n.locale.value, switched: false }
  }

  if (i18n.locale.value !== known) {
    console.warn(`[i18n] Locale "${tag}" was not applied, staying on ${i18n.locale.value}`)
    return { locale: i18n.locale.value, switched: false }
  }

  if (uiLocalizations && Object.keys(uiLocalizations).length > 0) {
    const canonicalMessages = i18n.getLocaleMessage(
      DEFAULT_LOCALE as TLocale,
    )
    const validMessages = filterValidLocalizationMessages(uiLocalizations, {
      referenceMessages: canonicalMessages,
    })
    if (Object.keys(validMessages).length > 0)
      i18n.mergeLocaleMessage(known, validMessages)
  }

  return { locale: tag, switched: true }
}
